import React, { FC, Fragment } from 'react';
import { Alert, IconButton, List, ListItem, ListItemText, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import RemoveIcon from '@mui/icons-material/Remove';
import { User } from 'models/user';
import { useMutation } from '@apollo/client';
import { removeLinkedExtTechTemplate } from 'apollo/templates/order/mutation.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { LinkedUser } from 'models/order';
import InfoText from '../info-text.component';

type ConnectedExternalTechniciansProps = {
    orderId: string;
    connectedExternalTechnicians: LinkedUser[];
    onRemove: (technician: User) => void;
};
const ConnectedExternalTechnicians: FC<ConnectedExternalTechniciansProps> = ({
    orderId,
    connectedExternalTechnicians,
    onRemove,
}: ConnectedExternalTechniciansProps) => {
    const { t: translate } = useTranslation('orders');
    const { showBoundary } = useDenthubErrorBoundary();

    const [removeLinkedExtTech] = useMutation(removeLinkedExtTechTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const onRemoveClick = (technician: User) => {
        onRemove(technician);
        removeLinkedExtTech({ variables: { orderId: orderId, userId: technician._id } });
    };

    return (
        <Fragment>
            {connectedExternalTechnicians.length > 0 ? (
                <Fragment>
                    <Typography variant="subtitle1">{translate('THIS_ORDER_IS_SHARED_WITH')}:</Typography>
                    <Paper variant="outlined">
                        <List dense>
                            {connectedExternalTechnicians.map((technician, index) => (
                                <ListItem
                                    key={technician.user._id}
                                    secondaryAction={
                                        <IconButton
                                            edge="end"
                                            onClick={() => {
                                                onRemoveClick(technician.user);
                                            }}
                                            data-test={`connected-external-technician.remove-btn-${index}`}
                                        >
                                            <RemoveIcon />
                                        </IconButton>
                                    }
                                >
                                    <ListItemText primary={technician.user.fullName} />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Fragment>
            ) : (
                <InfoText text={translate('THIS_ORDER_IS_NOT_SHARED')} />
            )}
        </Fragment>
    );
};

export default ConnectedExternalTechnicians;
